import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CommandParameterDescriptor,
  CommandParameters,
  CommandType,
  DeviceCommandDto,
  DeviceCommandRequest,
  DeviceCommandMutationInput,
  DeviceCommandUpdateInput,
  MutationOptions,
  MutationResult,
  Pagination,
  QueryOptions,
  QueryResult,
} from '@thingslog/repositories';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { QueryKeys } from '../enums/QueryKeys';

// Omitting deviceNumber 404s four command types and 500s MODBUS.
const withDeviceNumber = (deviceNumber: string): { params: { deviceNumber: string } } => ({
  params: { deviceNumber },
});

export interface CommandsQueryClient {
  getCommands: (
    deviceNumber: string,
    page: number,
    size: number,
    sort?: string
  ) => Promise<Pagination<DeviceCommandDto>>;
  useCommands: (
    deviceNumber: string,
    page: number,
    size: number,
    sort?: string,
    options?: QueryOptions<Pagination<DeviceCommandDto>>
  ) => QueryResult<Pagination<DeviceCommandDto>>;
  getCommandTypes: () => Promise<CommandType[]>;
  useCommandTypes: (options?: QueryOptions<CommandType[]>) => QueryResult<CommandType[]>;
  getCommandTypeParameters: (
    commandType: CommandType,
    deviceNumber: string
  ) => Promise<CommandParameterDescriptor[]>;
  useCommandTypeParameters: (
    commandType: CommandType | null,
    deviceNumber: string,
    options?: QueryOptions<CommandParameterDescriptor[]>
  ) => QueryResult<CommandParameterDescriptor[]>;
  updateCommand: (
    deviceNumber: string,
    commandId: number,
    commandState: 'CANCELED'
  ) => Promise<void>;
  useUpdateCommand: (
    options?: MutationOptions<void, DeviceCommandUpdateInput>
  ) => MutationResult<void, DeviceCommandUpdateInput>;
  createCommand: (
    deviceNumber: string,
    deviceCommandRequest: DeviceCommandRequest
  ) => Promise<number>;
  useCreateCommand: (
    options?: MutationOptions<number, DeviceCommandMutationInput>
  ) => MutationResult<number, DeviceCommandMutationInput>;
  pollLinkedDevices: (masterDeviceNumber: string, slaveDevices: string[]) => Promise<void>;
  usePollLinkedDevices: (
    options?: MutationOptions<void, { masterDeviceNumber: string; slaveDevices: string[] }>
  ) => MutationResult<void, { masterDeviceNumber: string; slaveDevices: string[] }>;
}

export function createCommandQueryClient(axios: AxiosInstance): CommandsQueryClient {
  return new CommandQueryImp(axios);
}

class CommandQueryImp {
  public constructor(private axios: AxiosInstance) {}

  public getCommands = async (
    deviceNumber: string,
    page: number,
    size: number,
    sort?: string
  ): Promise<Pagination<DeviceCommandDto>> => {
    return await this.axios
      .get(`/api/v2/devices/${deviceNumber}/commands`, { params: { page, size, sort } })
      .then((response: AxiosResponse<Pagination<DeviceCommandDto>>) => response.data);
  };

  public useCommands = (
    deviceNumber: string,
    page: number,
    size: number,
    sort?: string,
    options?: QueryOptions<Pagination<DeviceCommandDto>>
  ): QueryResult<Pagination<DeviceCommandDto>> => {
    return useQuery<Pagination<DeviceCommandDto>, AxiosError>(
      [QueryKeys.GetDeviceCommands, deviceNumber, page, size, sort],
      () => this.getCommands(deviceNumber, page, size, sort),
      options
    );
  };

  public getCommandTypes = async (): Promise<CommandType[]> => {
    return await this.axios
      .get(`/api/v2/command_descriptors`)
      .then((response: AxiosResponse<CommandType[]>) => response.data);
  };

  public useCommandTypes = (options?: QueryOptions<CommandType[]>): QueryResult<CommandType[]> => {
    return useQuery<CommandType[], AxiosError>(
      [QueryKeys.GetCommandTypes],
      () => this.getCommandTypes(),
      options
    );
  };

  public getCommandTypeParameters = async (
    commandType: CommandType,
    deviceNumber: string
  ): Promise<CommandParameterDescriptor[]> => {
    return await this.axios
      .get(`/api/v2/commands_descriptors/${commandType}/parameters`, withDeviceNumber(deviceNumber))
      .then((response: AxiosResponse<CommandParameterDescriptor[]>) => response.data);
  };

  public useCommandTypeParameters = (
    commandType: CommandType | null,
    deviceNumber: string,
    options?: QueryOptions<CommandParameterDescriptor[]>
  ): QueryResult<CommandParameterDescriptor[]> => {
    return useQuery<CommandParameterDescriptor[], AxiosError>(
      [QueryKeys.GetCommandTypeParameters, commandType, deviceNumber],
      () => this.getCommandTypeParameters(commandType as CommandType, deviceNumber),
      { ...options, enabled: commandType !== null && (options?.enabled ?? true) }
    );
  };

  public updateCommand = async (
    deviceNumber: string,
    commandId: number,
    commandState: 'CANCELED'
  ): Promise<void> => {
    return await this.axios
      .put(`/api/v2/devices/${deviceNumber}/commands/${commandId}`, { commandState })
      .then((response: AxiosResponse<void>) => response.data);
  };

  public useUpdateCommand = (
    options?: MutationOptions<void, DeviceCommandUpdateInput>
  ): MutationResult<void, DeviceCommandUpdateInput> => {
    return useMutation<void, AxiosError, DeviceCommandUpdateInput>(
      [QueryKeys.UpdateDeviceCommand],
      ({ deviceNumber, commandId, body }: DeviceCommandUpdateInput) =>
        this.updateCommand(deviceNumber, commandId, body.commandState),
      options
    );
  };

  public createCommand = async (
    deviceNumber: string,
    deviceCommandRequest: DeviceCommandRequest
  ): Promise<number> => {
    const { isNodeRedRequest, ...requestBody } = deviceCommandRequest;
    return await this.axios
      .post(`/api/v2/devices/${deviceNumber}/commands`, requestBody, {
        params: { isNodeRedRequest },
      })
      .then((response: AxiosResponse<number>) => {
        return response.data;
      });
  };

  public pollLinkedDevices = async (
    masterDeviceNumber: string,
    slaveDevices: string[]
  ): Promise<void> => {
    return await this.axios
      .post(`/api/v2/devices/${masterDeviceNumber}/commands/readAll`, {
        slaveNumbers: slaveDevices,
      })
      .then((response: AxiosResponse<void>) => {
        return response.data;
      });
  };

  public useCreateCommand = (
    options?: MutationOptions<number, DeviceCommandMutationInput>
  ): MutationResult<number, DeviceCommandMutationInput> => {
    return useMutation<number, AxiosError, DeviceCommandMutationInput>(
      [QueryKeys.CreateDeviceCommand],
      (createCommandVariables: DeviceCommandMutationInput) => {
        const { deviceNumber, deviceCommandRequest: createCommandBody } = createCommandVariables;
        return this.createCommand(deviceNumber, createCommandBody);
      },
      options
    );
  };

  public usePollLinkedDevices = (
    options?: MutationOptions<void, { masterDeviceNumber: string; slaveDevices: string[] }>
  ): MutationResult<void, { masterDeviceNumber: string; slaveDevices: string[] }> => {
    return useMutation<void, AxiosError, { masterDeviceNumber: string; slaveDevices: string[] }>(
      [QueryKeys.PollLinkedDevices],
      (pollLinkedDevicesVariables: { masterDeviceNumber: string; slaveDevices: string[] }) => {
        return this.pollLinkedDevices(
          pollLinkedDevicesVariables.masterDeviceNumber,
          pollLinkedDevicesVariables.slaveDevices
        );
      },
      options
    );
  };
}
